// Utility functions for handling image downloads and previews

/**
 * Downloads an image from either a URL or base64 data
 * @param {string} imageSource - Either a URL or base64 data string
 * @param {string} fileName - The desired filename for the download
 */
export const downloadImage = async (imageSource, fileName = "image") => {
  try {
    let blob;

    // Check if it's a base64 string
    if (imageSource.startsWith("data:image/")) {
      // Handle base64 image
      const response = await fetch(imageSource);
      blob = await response.blob();
    } else {
      // Handle URL image
      const response = await fetch(imageSource);
      if (!response.ok) {
        throw new Error("Failed to fetch image");
      }
      blob = await response.blob();
    }

    // Create download link
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;

    // Determine file extension from blob type or default to png
    const extension = blob.type.split("/")[1] || "png";
    link.download = `${fileName}.${extension}`;

    // Trigger download
    document.body.appendChild(link);
    link.click();

    // Cleanup
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    return true;
  } catch (error) {
    return false;
  }
};

/**
 * Extracts filename from image URL or generates a default one
 * @param {string} imageUrl - The image URL
 * @param {string} messageId - The message ID for fallback naming
 * @returns {string} - The filename without extension
 */
export const getImageFileName = (imageUrl, messageId) => {
  try {
    if (imageUrl.startsWith("data:image/")) {
      return `generated-image-${messageId}`;
    }

    const url = new URL(imageUrl);
    const pathname = url.pathname;
    const filename = pathname.split("/").pop();

    if (filename && filename.includes(".")) {
      // Remove extension for our function to add it back
      return filename.split(".").slice(0, -1).join(".");
    }

    return `image-${messageId}`;
  } catch (error) {
    return `image-${messageId}`;
  }
};

/**
 * Checks if an image source is a base64 data URL
 * @param {string} imageSource - The image source to check
 * @returns {boolean} - True if it's a base64 data URL
 */
export const isBase64Image = (imageSource) => {
  return (
    typeof imageSource === "string" && imageSource.startsWith("data:image/")
  );
};
